import { Fleet } from '@src/main/modules/parameters/domain/fleet-positions/fleet.interface';
import { FleetRepository } from '@src/main/modules/parameters/domain/fleet-positions/fleet.repository';
import { CreateUpdateFleetRequest } from '@src/main/modules/parameters/infrastructure/dto/createUpdateFleetRequest';
import { UpdateFleetPositionsRequest } from '@src/main/modules/parameters/infrastructure/dto/updateFleetPositionsRequest';
import {
  FleetEntity,
  toFleet,
} from '@src/main/modules/parameters/infrastructure/entity/fleet.entity';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';

const path = '/fleets';

const positions = '/positions';

export const createFleetHttpRepository = (tokenProvider: TokenProvider): FleetRepository =>
  new FleetHttpRepository(tokenProvider);

class FleetHttpRepository implements FleetRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findAll(): Promise<Fleet[]> {
    const token = await this.getAuthToken();
    const response = await api.get<FleetEntity[]>(`${path}/all`, token);
    return response.map(toFleet);
  }

  async findById(id: string): Promise<Fleet> {
    const token = await this.getAuthToken();
    const response = await api.get<FleetEntity>(`${path}/${id}`, token);
    return toFleet(response);
  }

  async create(fleet: CreateUpdateFleetRequest): Promise<Fleet> {
    const token = await this.getAuthToken();
    const response = await api.post<FleetEntity>(path, token, fleet);
    return toFleet(response);
  }

  async update(id: string, fleet: CreateUpdateFleetRequest): Promise<Fleet> {
    const token = await this.getAuthToken();
    const response = await api.put<FleetEntity>(`${path}/${id}`, token, fleet);
    return toFleet(response);
  }

  async updatePositions(id: string, fleet: UpdateFleetPositionsRequest): Promise<Fleet> {
    const token = await this.getAuthToken();
    const response = await api.put<FleetEntity>(`${path}${positions}/${id}`, token, fleet);
    return toFleet(response);
  }

  async delete(id: string): Promise<void> {
    const token = await this.getAuthToken();
    await api.delete(`${path}/${id}`, token);
  }
}
